import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import { ActionType } from '@unifyapps/defs/types/action';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import type { OnActionType } from '../../context/ActionsProvider/context';
import { useControlBlockMethod } from './actions/controlBlockMethod';
import { useControlBlockSelection } from './actions/controlBlockSelection';
import { useControlBottomSheetActions } from './actions/controlBottomSheet';
import { useControlBuild } from './actions/controlBuild';
import { useControlCleverTap } from './actions/controlCleverTap';
import {
  useControlGlobalPageDataSource,
  useControlPageDataSource,
} from './actions/controlDataSource';
import { useControlDevicePermissions } from './actions/controlDevicePermissions';
import { useControlDrawerActions } from './actions/controlDrawer';
import { useControlDrawerNavigatorActions } from './actions/controlDrawerNavigator';
import { useControlMediaUploads } from './actions/controlMediaUploads';
import { useControlMediaViewer } from './actions/controlMediaViewer';
import { useControlModalActions } from './actions/controlModal';
import { useControlNotification } from './actions/controlNotification';
import useControlOfflineMode from './actions/controlOfflineMode/useControlOfflineMode';
import { useControlPushNotifications } from './actions/controlPushNotifications';
import { useControlRazorpay } from './actions/controlRazorpay';
import { useControlStep } from './actions/controlStep/useControlStep';
import { useControlUAEPass } from './actions/controlUAEPass';
import { useControlUser } from './actions/controlUser';
import { useControlWalkthrough } from './actions/controlWalkthrough/useControlWalkthrough';
import { useControlYellowAI } from './actions/controlYellowAI';
import { useCopyToClipboardAction } from './actions/copyToClipboard';
import { useDownloadFile } from './actions/downloadFile';
import { useEmitPageEvent } from './actions/emitPageEvent';
import { useEvalJS } from './actions/evalJS';
import { useExecuteScript } from './actions/executeScript';
import { useExportData } from './actions/exportData';
import { useImportObjects } from './actions/importObjects';
import { useLogin } from './actions/login';
import { useLogToDebugger } from './actions/logToDebugger';
import { useManageBiometrics } from './actions/manageBiometrics';
import useMobileUtils from './actions/mobileUtils/useMobileUtils';
import { useNavigate } from './actions/navigate';
import { useNavigateToPage, useSetURLParameters } from './actions/navigateToPage';
import { useOpenShareDialog } from './actions/openShareDialog';
import { useSendCustomEvent } from './actions/sendCustomEvent';
import useSendMQTTEvent from './actions/sendMQTTEvent/useSendMQTTEvent';
import { useSetInterfaceSessionStorage } from './actions/setInterfaceSessionStorage';
import { useSetLocalStorage } from './actions/setLocalStorage';
import { useSetGlobalPageVariable, useSetPageVariable } from './actions/setPageVariable';
import { useShareEntityRecord } from './actions/shareEntityRecord';
import { useShowNotification } from './actions/showNotification';
import { useFormActions } from './actions/submitForm';
import useTriggerFunction, {
  useTriggerGlobalPageFunction,
} from './actions/triggerFunction/useTriggerFunction';
import { SHOULD_NOT_PROPAGATE_UP, SHOULD_PROPAGATE_UP } from './const';

export function useGlobalPageActions() {
  const { navigate } = useNavigate();
  const { login } = useLogin();
  const { onEvalJS } = useEvalJS();
  const { onLogToDebugger } = useLogToDebugger();
  const { onShowNotification } = useShowNotification();
  const { onControlDataSource } = useControlGlobalPageDataSource();
  const { setDevicePermissions } = useControlDevicePermissions();
  const { onBiometricAction } = useManageBiometrics();
  const { onSetLocalStorage } = useSetLocalStorage();
  const { handleCopyAction } = useCopyToClipboardAction();
  const { controlWalkthrough } = useControlWalkthrough();
  const { controlStep } = useControlStep();
  const { onSetPageVariable } = useSetGlobalPageVariable();
  const { sendMQTTEvent } = useSendMQTTEvent();
  const { sendCustomEvent } = useSendCustomEvent();
  const { onExecuteScript } = useExecuteScript();
  const { onControlBuild } = useControlBuild();
  const { onTriggerFunction } = useTriggerGlobalPageFunction();
  const { onSetInterfaceSessionStorage } = useSetInterfaceSessionStorage();
  const { onControlOfflineMode } = useControlOfflineMode();
  const { onMobileUtils } = useMobileUtils();
  const { onControlPushNotifications } = useControlPushNotifications();
  const { onControlUAEPass } = useControlUAEPass();
  const { onControlRazorpay } = useControlRazorpay();
  const { onControlCleverTap } = useControlCleverTap();
  const { onControlYellowAI } = useControlYellowAI();

  const onAction: OnActionType = useEventCallback<OnActionType>((args) => {
    const actionType = args.action.actionType;

    switch (actionType) {
      case ActionType.Navigate:
        return navigate(args);
      case ActionType.Login:
        return login(args);
      case ActionType.EvalJS:
        return onEvalJS(args);
      case ActionType.LogToDebugger:
        return onLogToDebugger(args);
      case ActionType.ExecuteScript:
        return onExecuteScript(args);
      case ActionType.ShowNotification:
        return onShowNotification(args);
      case ActionType.SetLocalStorage:
        return onSetLocalStorage(args);
      case ActionType.ControlDevicePermissions:
        return setDevicePermissions(args);
      case ActionType.ControlPushNotifications:
        return onControlPushNotifications(args);
      case ActionType.CopyToClipboard:
        return handleCopyAction(args);
      // keeping data source actions here as well because we can trigger global page datasource in success
      // handler of global data source
      case ActionType.ControlDataSource:
        return onControlDataSource(args);
      case ActionType.SetPageVariable:
        return onSetPageVariable(args);
      case ActionType.TriggerFunction:
        return onTriggerFunction(args);
      case ActionType.ControlWalkthrough:
        return controlWalkthrough(args);
      case ActionType.ControlStep:
        return controlStep(args);
      case ActionType.ManageBiometrics:
        return onBiometricAction(args);
      case ActionType.SendMQTTEvent:
        return sendMQTTEvent(args);
      case ActionType.SendCustomEvent:
        return sendCustomEvent(args);
      case ActionType.ControlBuild:
        return onControlBuild(args);
      case ActionType.SetInterfaceSessionStorage:
        return onSetInterfaceSessionStorage(args);
      case ActionType.ControlOfflineMode:
        return onControlOfflineMode(args);
      case ActionType.MobileUtils:
        return onMobileUtils(args);
      case ActionType.ControlUAEPass:
        return onControlUAEPass(args);
      case ActionType.ControlRazorpay:
        return onControlRazorpay(args);
      case ActionType.ControlCleverTap:
        return onControlCleverTap(args);
      case ActionType.ControlYellowAI:
        return onControlYellowAI(args);
      default:
        // Not propagating up, because this is a app action provider
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }
  });

  return { onAction };
}

export function usePageActions({ emitPageEvent }: { emitPageEvent?: EmitEventType } = {}) {
  const { onControlBlockMethod } = useControlBlockMethod();
  const { controlBlockSelection } = useControlBlockSelection();
  const { onSetPageVariable } = useSetPageVariable();
  const { onControlDataSource } = useControlPageDataSource();
  const { onControlModal } = useControlModalActions();
  const { onControlDrawer } = useControlDrawerActions();
  const { onControlDrawerNavigator } = useControlDrawerNavigatorActions();
  const { onControlBottomSheet } = useControlBottomSheetActions();
  const { onSubmitForm } = useFormActions();
  const { navigateToPage, back } = useNavigateToPage();
  const { onControlMediaUploads } = useControlMediaUploads();
  const { setURLParameters } = useSetURLParameters();
  const { importObjects } = useImportObjects();
  const { shareEntityRecord } = useShareEntityRecord();
  const { controlMediaViewer } = useControlMediaViewer();
  const { onEmitPageEvent } = useEmitPageEvent({
    emitPageEvent,
  });
  const { onExportData } = useExportData();
  const { onControlNotification } = useControlNotification();
  const { onControlUser } = useControlUser();
  const { onControlUAEPass } = useControlUAEPass();
  const { onControlRazorpay } = useControlRazorpay();
  const { onControlCleverTap } = useControlCleverTap();
  const { onControlYellowAI } = useControlYellowAI();
  const { onControlBuild } = useControlBuild();
  const { openShareDialog } = useOpenShareDialog();
  const { onDownloadFile } = useDownloadFile();
  const { onBiometricAction } = useManageBiometrics();
  const { onTriggerFunction } = useTriggerFunction();
  const { onControlOfflineMode } = useControlOfflineMode();
  const { onMobileUtils } = useMobileUtils();
  // page-scope script/eval actions — handled here so pulse errors are attributed
  // to the page scope (appName > Homepage) rather than the app scope.
  const { onExecuteScript } = useExecuteScript();
  const { onEvalJS } = useEvalJS();
  const { onLogToDebugger } = useLogToDebugger();

  const onAction: OnActionType = useEventCallback<OnActionType>((args) => {
    const actionType = args.action.actionType;

    switch (actionType) {
      case ActionType.OpenShareDialog:
        return openShareDialog(args);
      case ActionType.ManageBiometrics:
        return onBiometricAction(args);
      case ActionType.NavigateBack:
        return back(args);
      case ActionType.EmitPageEvent:
        return onEmitPageEvent(args);
      case ActionType.ControlBlockMethod:
        return onControlBlockMethod(args);
      case ActionType.ControlBlockSelection:
        return controlBlockSelection(args);
      case ActionType.SetPageVariable:
        return onSetPageVariable(args);
      case ActionType.ControlDataSource:
        return onControlDataSource(args);
      case ActionType.ControlModal:
        return onControlModal(args);
      case ActionType.ControlDrawer:
        return onControlDrawer(args);
      case ActionType.ControlDrawerNavigator:
        return onControlDrawerNavigator(args);
      case ActionType.ControlBottomSheet:
        return onControlBottomSheet(args);
      case ActionType.NavigateToPage:
        return navigateToPage(args);
      case ActionType.SetURLParameters:
        return setURLParameters(args);
      case ActionType.SubmitForm:
        return onSubmitForm(args);
      case ActionType.ImportObjects:
        return importObjects(args);
      case ActionType.ShareEntityRecord:
        return shareEntityRecord(args);
      case ActionType.ControlMediaViewer:
        return controlMediaViewer(args);
      case ActionType.ExportData:
        return onExportData(args);
      case ActionType.ControlNotification:
        return onControlNotification(args);
      // ControlUserLocation shares controlUser's payload shape + runtime; only the picker differs
      case ActionType.ControlUser:
      case ActionType.ControlUserLocation:
        return onControlUser(args);
      case ActionType.ControlUAEPass:
        return onControlUAEPass(args);
      case ActionType.ControlCleverTap:
        return onControlCleverTap(args);
      case ActionType.ControlYellowAI:
        return onControlYellowAI(args);
      case ActionType.ControlBuild:
        return onControlBuild(args);
      case ActionType.DownloadFile:
        return onDownloadFile(args);
      case ActionType.ControlMediaUploads:
        return onControlMediaUploads(args);
      case ActionType.TriggerFunction:
        return onTriggerFunction(args);
      case ActionType.ControlOfflineMode:
        return onControlOfflineMode(args);
      case ActionType.MobileUtils:
        return onMobileUtils(args);
      case ActionType.ControlRazorpay:
        return onControlRazorpay(args);
      case ActionType.ExecuteScript:
        return onExecuteScript(args);
      case ActionType.EvalJS:
        return onEvalJS(args);
      case ActionType.LogToDebugger:
        return onLogToDebugger(args);
      default:
        // If actionType is not found, we should propagate up,
        // remaining actionType are handled by the global page actions
        return Promise.resolve(SHOULD_PROPAGATE_UP);
    }
  });

  return { onAction };
}
